// pages/canada/ProvincePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Image, Spinner } from 'react-bootstrap';
import { Pen, People, ChevronRight, Newspaper } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import DeskCarousel from '../carousel/DeskCarousel';

import {
    getProvincialInfo,
    getProvincialUpdateFeed,
    getProvincialFeedsByBranch,
  } from '../../utils/canadaFeedsConfig';
import HomeProvinceButton from '../notifications/HomeProvinceButton';
import './ProvincePage.css';

const JudicialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill="currentColor">
    <path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/>
  </svg>
);

const ProvincePage = () => {
  const { t } = useTranslation('common');
  const { provinceName } = useParams();
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // normalize slug to underscore form
  const provinceKey = String(provinceName || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  const province = getProvincialInfo(provinceKey);

  if (!province) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div>
          <Spinner id='spinner' animation="grow" variant="light" />
          <p style={{ color: 'var(--text-primary)' }}>{t('common:provinceNotFound', 'Province not found: {{provinceName}}', { provinceName })}</p>
        </div>
      </div>
    );
  }

  const updatesFeeds = getProvincialUpdateFeed(provinceKey);
  const legislativeFeeds = getProvincialFeedsByBranch(provinceKey, 'legislative');
  const executiveFeeds = getProvincialFeedsByBranch(provinceKey, 'executive');
  const judicialFeeds = getProvincialFeedsByBranch(provinceKey, 'judicial');

  // Transform feeds into the format DeskCarousel expects
  const transformFeedsForCarousel = (feeds, itemCount = 5) => {
    if (!feeds || feeds.length === 0) return [];
    
    const result = [];
    feeds.forEach(feed => {
      for (let i = 0; i < itemCount; i++) {
        result.push({
          type: 'normal',
          id: `${feed.id}-${i}`,
          title: feed.name,
          apiEndpoint: feed.apiParams.replace(/skip=\d+/, `skip=${i}`),
          feedName: feed.name,
          branch: feed.feedBranch,
          topic: feed.feedTopic,
          buttonName: feed.buttonName,
          viewAllLink: null
        });
      }
    });
    return result;
  };

  // MOBILE LAYOUT
  if (isMobile) {
    return (
      <div id="homebg" className="province-page-root">
        <div style={{ marginTop: '45px', paddingTop: '5px' }}>
 
          {/* Province Header with flag and Home Province Button */}
      

          {/* Updates Section */}
          <div className="province-branch-section">
            <div className="branch-header">
              <div className="branch-title">
                <Newspaper />
                <span>{t('common:updates', 'Updates')}</span>
                <ChevronRight className="branch-chevron" />
              </div>
            </div>
            {updatesFeeds?.length ? (
              <MobileFeedCarousel feeds={updatesFeeds} />
            ) : (
              <div className="coming-soon-box">{t('common:noUpdatesAvailable', 'No updates available')}</div>
            )}
          </div>

          {/* Legislative Section */}
          <div className="province-branch-section">
            <div className="branch-header">
              <div className="branch-title">
                <Pen />
                <span>{t('common:section_legislative', 'Legislative')}</span>
                
              </div>
            </div>
            {legislativeFeeds?.length ? (
              <MobileFeedCarousel feeds={legislativeFeeds} />
            ) : (
              <div className="coming-soon-box">{t('common:comingSoon', 'Coming Soon')}</div>
            )}
          </div>

          {/* Executive Section */}
          <div className="province-branch-section">
            <div className="branch-header">
              <div className="branch-title">
                <People />
                <span>{t('common:section_executive', 'Executive')}</span>
                
              </div>
            </div>
            {executiveFeeds?.length ? (
              <MobileFeedCarousel feeds={executiveFeeds} />
            ) : (
              <div className="coming-soon-box">{t('common:comingSoon', 'Coming Soon')}</div>
            )}
          </div>

          {/* Judicial Section */}
          <div className="province-branch-section">
            <div className="branch-header">
              <div className="branch-title">
                <JudicialIcon />
                <span>{t('common:section_judicial', 'Judicial')}</span>
                
              </div>
            </div>
            {judicialFeeds?.length ? (
              <MobileFeedCarousel feeds={judicialFeeds} />
            ) : (
              <div className="coming-soon-box">{t('common:comingSoon', 'Coming Soon')}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DESKTOP LAYOUT - Updates centered above three branches
  return (
    <div id="homebg" className="province-page-root">
      <br />
      <br />
      <Container fluid>
        {/* Province Header with flag and Home Province Button */}
        {/* <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center justify-content-between">
              <h5 id="provinceFlagHeadingMobile">
                <Image
                  id="provinceFlagMobile"
                  src={province.flagImage}
                  alt={`${province.fullName} flag`}
                />
                {province.fullName}
              </h5>
              <HomeProvinceButton
                provinceKey={provinceKey} 
                provinceName={province.fullName}
              />
            </div>
          </Col>
        </Row> */}

        {/* Updates Row - Centered above branches */}
        {updatesFeeds?.length > 0 && (
          <Row className="mb-4 justify-content-center">
            <Col md={6} lg={5}>
              <DeskCarousel 
                title={t('common:updates', 'Updates')}
                icon={<Newspaper />}
                sections={transformFeedsForCarousel(updatesFeeds)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            </Col>
          </Row>
        )}
        
        {/* Branches Row - Three columns */}
        <Row id="branchesRow">
          {/* Legislative Section */}
          <Col md={4}>
            {legislativeFeeds?.length > 0 ? (
              <DeskCarousel 
                title={t('common:section_legislative', 'Legislative')}
                icon={<Pen />}
                sections={transformFeedsForCarousel(legislativeFeeds)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <Pen /> {t('common:section_legislative', 'Legislative')}
                </h5>
                <div style={{ 
                  padding: '20px', 
                  borderRadius: '8px'
                }}>
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
          
          {/* Executive Section */}
          <Col md={4}>
            {executiveFeeds?.length > 0 ? (
              <DeskCarousel
                title={t('common:section_executive', 'Executive')}
                icon={<People />}
                sections={transformFeedsForCarousel(executiveFeeds)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <People /> {t('common:section_executive', 'Executive')}
                </h5>
                <div style={{ 
                  padding: '20px', 
                  borderRadius: '8px'
                }}>
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
          
          {/* Judicial Section */}
          <Col md={4}>
            {judicialFeeds?.length > 0 ? (
              <DeskCarousel
                title={t('common:section_judicial', 'Judicial')}
                icon={<JudicialIcon />}
                sections={transformFeedsForCarousel(judicialFeeds)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <JudicialIcon /> {t('common:section_judicial', 'Judicial')}
                </h5>
                <div style={{ 
                  padding: '20px', 
                  borderRadius: '8px'
                }}>
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ProvincePage;